/**
 * A token-by-token reading of a JavaScript regular expression, for the regex tester's
 * explanation panel. It covers the syntax people actually write — literals, escapes,
 * character classes, groups and lookarounds, backreferences, quantifiers, anchors and
 * alternation — and returns structured tokens; the widget turns each kind into words.
 *
 * It is a reader, not a validator: the engine decides what is legal, and anything this
 * tokenizer does not recognise comes back as `unknown` rather than an error.
 */

export type ExplainKind =
  | "literal"
  | "any"
  | "anyDotAll"
  | "digit"
  | "notDigit"
  | "word"
  | "notWord"
  | "space"
  | "notSpace"
  | "boundary"
  | "notBoundary"
  | "start"
  | "startLine"
  | "end"
  | "endLine"
  | "escapedChar"
  | "special"
  | "codePoint"
  | "control"
  | "property"
  | "notProperty"
  | "class"
  | "negatedClass"
  | "group"
  | "namedGroup"
  | "nonCapturing"
  | "modifiers"
  | "lookahead"
  | "negativeLookahead"
  | "lookbehind"
  | "negativeLookbehind"
  | "groupEnd"
  | "alternation"
  | "backreference"
  | "namedBackreference"
  | "zeroOrMore"
  | "oneOrMore"
  | "optional"
  | "exactly"
  | "atLeast"
  | "between"
  | "unknown";

export interface ExplainToken {
  kind: ExplainKind;
  /** The characters of the pattern this token covers. */
  source: string;
  /** Group nesting depth, for indentation. */
  depth: number;
  /** For quantifiers: `?` after it — match as few as possible. */
  lazy: boolean;
  /** Values for the description: text, group number or name, counts, code point… */
  values: Record<string, string | number>;
}

export interface RegexExplanation {
  tokens: ExplainToken[];
  /** A quantified group that itself contains an unbounded quantifier: `(a+)+`. */
  nestedQuantifier: boolean;
}

/** Escapes that stand for a named control character. */
const SPECIAL_ESCAPES: Record<string, string> = {
  n: "lineFeed",
  r: "carriageReturn",
  t: "tab",
  f: "formFeed",
  v: "verticalTab",
  "0": "nul",
};

const CLASS_ESCAPES: Record<string, ExplainKind> = {
  d: "digit",
  D: "notDigit",
  w: "word",
  W: "notWord",
  s: "space",
  S: "notSpace",
  b: "boundary",
  B: "notBoundary",
};

const RE_BRACE_QUANTIFIER = /^\{(\d+)(,(\d*))?\}/;
const RE_HEX2 = /^x([0-9a-fA-F]{2})/;
const RE_HEX4 = /^u([0-9a-fA-F]{4})/;
const RE_CODE_POINT = /^u\{([0-9a-fA-F]{1,6})\}/;
const RE_CONTROL = /^c([A-Za-z])/;
const RE_PROPERTY = /^([pP])\{([^}]+)\}/;
const RE_BACKREFERENCE = /^([1-9]\d*)/;
const RE_NAMED_BACKREFERENCE = /^k<([^>]+)>/;
const RE_NAMED_GROUP = /^\(\?<([A-Za-z_$][\w$]*)>/;
const RE_MODIFIERS = /^\(\?([ims]*)(?:-([ims]+))?:/;

interface GroupFrame {
  /** Whether the group contains an unbounded quantifier anywhere inside. */
  unbounded: boolean;
}

function hex(code: number): string {
  return `U+${code.toString(16).toUpperCase().padStart(4, "0")}`;
}

class Explainer {
  readonly pattern: string;
  readonly dotAll: boolean;
  readonly multiline: boolean;
  readonly unicodeSets: boolean;
  readonly totalGroups: number;
  readonly tokens: ExplainToken[] = [];
  readonly frames: GroupFrame[] = [{ unbounded: false }];
  pos = 0;
  depth = 0;
  groupCount = 0;
  nestedQuantifier = false;
  /** The group frame that just closed, while its quantifier may still follow. */
  justClosed: GroupFrame | null = null;

  constructor(pattern: string, flags: string) {
    this.pattern = pattern;
    this.dotAll = flags.includes("s");
    this.multiline = flags.includes("m");
    this.unicodeSets = flags.includes("v");
    this.totalGroups = countCapturingGroups(pattern);
  }

  push(
    kind: ExplainKind,
    source: string,
    values: Record<string, string | number> = {}
  ): void {
    this.tokens.push({ kind, source, depth: this.depth, lazy: false, values });
  }

  rest(): string {
    return this.pattern.slice(this.pos);
  }

  run(): RegexExplanation {
    while (this.pos < this.pattern.length) {
      const closedBefore = this.justClosed;
      this.step();
      if (this.justClosed === closedBefore) {
        this.justClosed = null;
      }
    }
    return { tokens: this.tokens, nestedQuantifier: this.nestedQuantifier };
  }

  step(): void {
    const ch = this.pattern[this.pos];
    if (this.readQuantifier()) {
      return;
    }
    switch (ch) {
      case "\\":
        this.readEscape();
        return;
      case "[":
        this.readClass();
        return;
      case "(":
        this.readGroupStart();
        return;
      case ")":
        this.readGroupEnd();
        return;
      case "|":
        this.pos += 1;
        this.push("alternation", "|");
        return;
      case ".":
        this.pos += 1;
        this.push(this.dotAll ? "anyDotAll" : "any", ".");
        return;
      case "^":
        this.pos += 1;
        this.push(this.multiline ? "startLine" : "start", "^");
        return;
      case "$":
        this.pos += 1;
        this.push(this.multiline ? "endLine" : "end", "$");
        return;
      default:
        this.readLiteral();
    }
  }

  /** `*`, `+`, `?`, `{n}`, `{n,}`, `{n,m}` — each optionally followed by `?`. */
  readQuantifier(): boolean {
    const ch = this.pattern[this.pos];
    let kind: ExplainKind | null = null;
    let source = ch;
    const values: Record<string, string | number> = {};
    if (ch === "*") {
      kind = "zeroOrMore";
    } else if (ch === "+") {
      kind = "oneOrMore";
    } else if (ch === "?") {
      kind = "optional";
    } else if (ch === "{") {
      const match = RE_BRACE_QUANTIFIER.exec(this.rest());
      if (!match) {
        return false;
      }
      source = match[0];
      values.min = Number(match[1]);
      if (match[2] === undefined) {
        kind = "exactly";
      } else if (match[3] === "") {
        kind = "atLeast";
      } else {
        kind = "between";
        values.max = Number(match[3]);
      }
    }
    if (kind === null) {
      return false;
    }
    this.pos += source.length;
    const lazy = this.pattern[this.pos] === "?";
    if (lazy) {
      this.pos += 1;
      source += "?";
    }
    this.splitLastLiteral();
    this.tokens.push({ kind, source, depth: this.depth, lazy, values });
    const unbounded =
      kind === "zeroOrMore" || kind === "oneOrMore" || kind === "atLeast";
    if (unbounded && this.justClosed?.unbounded) {
      this.nestedQuantifier = true;
    }
    if (unbounded) {
      for (const frame of this.frames) {
        frame.unbounded = true;
      }
    }
    return true;
  }

  /** A quantifier applies to one character: `abc+` repeats only the `c`. */
  splitLastLiteral(): void {
    const last = this.tokens.at(-1);
    if (last?.kind !== "literal") {
      return;
    }
    const chars = Array.from(String(last.values.text));
    if (chars.length < 2) {
      return;
    }
    const tail = chars.pop() ?? "";
    last.values.text = chars.join("");
    last.source = last.source.slice(0, last.source.length - tail.length);
    this.push("literal", tail, { text: tail });
  }

  readLiteral(): void {
    const ch = Array.from(this.rest())[0] ?? "";
    this.pos += ch.length;
    const last = this.tokens.at(-1);
    if (last?.kind === "literal" && last.depth === this.depth) {
      last.source += ch;
      last.values.text = `${last.values.text}${ch}`;
      return;
    }
    this.push("literal", ch, { text: ch });
  }

  readEscape(): void {
    const after = this.pattern.slice(this.pos + 1);
    const next = after[0];
    if (next === undefined) {
      this.pos += 1;
      this.push("unknown", "\\");
      return;
    }
    const classKind = CLASS_ESCAPES[next];
    if (classKind) {
      this.pos += 2;
      this.push(classKind, `\\${next}`);
      return;
    }
    if (this.readValueEscape(after)) {
      return;
    }
    this.pos += 2;
    const special = SPECIAL_ESCAPES[next];
    if (special) {
      this.push("special", `\\${next}`, { name: special });
      return;
    }
    this.push("escapedChar", `\\${next}`, { text: next });
  }

  /** Escapes that carry a value: code points, controls, properties, backreferences. */
  readValueEscape(after: string): boolean {
    const codePoint =
      RE_CODE_POINT.exec(after) ?? RE_HEX4.exec(after) ?? RE_HEX2.exec(after);
    if (codePoint) {
      this.pos += 1 + codePoint[0].length;
      this.push("codePoint", `\\${codePoint[0]}`, {
        code: hex(Number.parseInt(codePoint[1], 16)),
      });
      return true;
    }
    const control = RE_CONTROL.exec(after);
    if (control) {
      this.pos += 1 + control[0].length;
      this.push("control", `\\${control[0]}`, {
        letter: control[1].toUpperCase(),
      });
      return true;
    }
    const property = RE_PROPERTY.exec(after);
    if (property) {
      this.pos += 1 + property[0].length;
      this.push(
        property[1] === "p" ? "property" : "notProperty",
        `\\${property[0]}`,
        {
          name: property[2],
        }
      );
      return true;
    }
    const named = RE_NAMED_BACKREFERENCE.exec(after);
    if (named) {
      this.pos += 1 + named[0].length;
      this.push("namedBackreference", `\\${named[0]}`, { name: named[1] });
      return true;
    }
    const numbered = RE_BACKREFERENCE.exec(after);
    if (numbered && Number(numbered[1]) <= this.totalGroups) {
      this.pos += 1 + numbered[0].length;
      this.push("backreference", `\\${numbered[0]}`, {
        group: Number(numbered[1]),
      });
      return true;
    }
    return false;
  }

  readClass(): void {
    const start = this.pos;
    const negated = this.pattern[this.pos + 1] === "^";
    let index = this.pos + (negated ? 2 : 1);
    let nesting = 0;
    const items: string[] = [];
    while (index < this.pattern.length) {
      const ch = this.pattern[index];
      if (ch === "\\") {
        items.push(this.pattern.slice(index, index + 2));
        index += 2;
        continue;
      }
      if (ch === "[" && this.unicodeSets) {
        nesting += 1;
      } else if (ch === "]") {
        if (nesting === 0) {
          break;
        }
        nesting -= 1;
      }
      items.push(ch);
      index += 1;
    }
    this.pos = Math.min(index + 1, this.pattern.length);
    const source = this.pattern.slice(start, this.pos);
    this.push(negated ? "negatedClass" : "class", source, {
      items: describeClassItems(items),
    });
  }

  readGroupStart(): void {
    const rest = this.rest();
    const frame: GroupFrame = { unbounded: false };
    const named = RE_NAMED_GROUP.exec(rest);
    if (named) {
      this.groupCount += 1;
      this.openGroup("namedGroup", named[0], frame, {
        name: named[1],
        group: this.groupCount,
      });
      return;
    }
    const lookaround: [string, ExplainKind][] = [
      ["(?=", "lookahead"],
      ["(?!", "negativeLookahead"],
      ["(?<=", "lookbehind"],
      ["(?<!", "negativeLookbehind"],
      ["(?:", "nonCapturing"],
    ];
    for (const [prefix, kind] of lookaround) {
      if (rest.startsWith(prefix)) {
        this.openGroup(kind, prefix, frame, {});
        return;
      }
    }
    const modifiers = RE_MODIFIERS.exec(rest);
    if (modifiers) {
      this.openGroup("modifiers", modifiers[0], frame, {
        on: modifiers[1] ?? "",
        off: modifiers[2] ?? "",
      });
      return;
    }
    this.groupCount += 1;
    this.openGroup("group", "(", frame, { group: this.groupCount });
  }

  openGroup(
    kind: ExplainKind,
    source: string,
    frame: GroupFrame,
    values: Record<string, string | number>
  ): void {
    this.pos += source.length;
    this.push(kind, source, values);
    this.frames.push(frame);
    this.depth += 1;
  }

  readGroupEnd(): void {
    this.pos += 1;
    this.depth = Math.max(0, this.depth - 1);
    this.push("groupEnd", ")");
    const frame = this.frames.length > 1 ? this.frames.pop() : undefined;
    this.justClosed = frame ?? null;
  }
}

/** Class contents as short readable items: `a-z` becomes `a–z`. */
function describeClassItems(items: string[]): string {
  const parts: string[] = [];
  let index = 0;
  while (index < items.length) {
    const item = items[index];
    if (items[index + 1] === "-" && index + 2 < items.length) {
      parts.push(`${item}–${items[index + 2]}`);
      index += 3;
      continue;
    }
    parts.push(item);
    index += 1;
  }
  return parts.join(" ");
}

/** Capturing groups: plain `(` and named `(?<name>`, skipping escapes and classes. */
function countCapturingGroups(pattern: string): number {
  let count = 0;
  let inClass = false;
  for (let index = 0; index < pattern.length; index += 1) {
    const ch = pattern[index];
    if (ch === "\\") {
      index += 1;
    } else if (inClass) {
      inClass = ch !== "]";
    } else if (ch === "[") {
      inClass = true;
    } else if (
      ch === "(" &&
      (pattern[index + 1] !== "?" || RE_NAMED_GROUP.test(pattern.slice(index)))
    ) {
      count += 1;
    }
  }
  return count;
}

export function explainRegex(pattern: string, flags: string): RegexExplanation {
  return new Explainer(pattern, flags).run();
}
